import axios from 'axios';

class WebRTCService {
  constructor() {
    this.baseURL = process.env.REACT_APP_WORKER_URL || 'http://localhost:8787';
    this.peerConnections = new Map(); // Map of hostId -> RTCPeerConnection
    this.localStream = null;
    this.localScreenStream = null;
    this.ws = null;
    this.currentStreamId = null;
    this.currentUserType = null;
    this.hostInfo = null;
    
    // Event handlers - can be overridden
    this.onRemoteStream = null;
    this.onHostJoined = null;
    this.onHostLeft = null;
    this.onHostMediaStateChanged = null;
    this.onConnectionStateChanged = null;
    this.onChatMessage = null;
    
    // ICE servers configuration
    this.iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];
  }

  // Initialize WebSocket connection for real-time signaling
  async connectWebSocket(streamId) {
    try {
      const wsUrl = `ws://localhost:8787/stream/${streamId}/ws`;
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected for stream:', streamId);
        this.onConnectionStateChanged?.('connected');
      };
      
      this.ws.onmessage = (event) => {
        this.handleWebSocketMessage(JSON.parse(event.data));
      };
      
      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.onConnectionStateChanged?.('disconnected');
      };
      
      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onConnectionStateChanged?.('error');
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      throw error;
    }
  }

  // Handle incoming WebSocket messages
  handleWebSocketMessage(message) {
    console.log('Received WebSocket message:', message);
    
    switch (message.type) {
      case 'host-joined':
        this.handleHostJoined(message.data);
        break;
      case 'host-left':
        this.handleHostLeft(message.data);
        break;
      case 'webrtc-offer':
        this.handleWebRTCOffer(message.data);
        break;
      case 'webrtc-answer':
        this.handleWebRTCAnswer(message.data);
        break;
      case 'webrtc-ice-candidate':
        this.handleWebRTCIceCandidate(message.data);
        break;
      case 'host-media-state':
        this.handleHostMediaStateChanged(message.data);
        break;
      case 'chat-message':
        this.onChatMessage?.(message.data);
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  }

  // Join a stream as a host or viewer
  async joinStream(streamId, userType = 'viewer', hostInfo = null) {
    try {
      this.currentStreamId = streamId;
      this.currentUserType = userType;
      this.hostInfo = hostInfo;
      
      // Connect to WebSocket for real-time communication
      await this.connectWebSocket(streamId);
      
      // Send join message
      this.sendWebSocketMessage({
        type: 'join-stream',
        data: {
          streamId,
          userType,
          hostInfo: this.hostInfo,
        }
      });
      
      return true;
    } catch (error) {
      console.error('Failed to join stream:', error);
      throw error;
    }
  }

  // Leave the current stream
  leaveStream() {
    // Close all peer connections
    this.peerConnections.forEach((pc, hostId) => {
      pc.close();
    });
    this.peerConnections.clear();
    
    // Stop local streams
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    
    if (this.localScreenStream) {
      this.localScreenStream.getTracks().forEach(track => track.stop());
      this.localScreenStream = null;
    }
    
    // Close WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.currentStreamId = null;
    this.currentUserType = null;
    this.hostInfo = null;
  }

  // Start camera/microphone capture
  async startCamera(constraints = { video: true, audio: true }) {
    try {
      // Stop existing stream if any
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop());
      }
      
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Add tracks to all existing peer connections
      this.peerConnections.forEach((pc, hostId) => {
        this.localStream.getTracks().forEach(track => {
          pc.addTrack(track, this.localStream);
        });
      });
      
      // Notify other hosts about media state change
      this.notifyMediaStateChange();
      
      return this.localStream;
    } catch (error) {
      console.error('Failed to start camera:', error);
      throw error;
    }
  }

  // Stop camera
  stopCamera() {
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach(track => track.stop());
      this.notifyMediaStateChange();
    }
  }

  // Toggle microphone
  toggleMicrophone() {
    if (this.localStream) {
      const audioTracks = this.localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      this.notifyMediaStateChange();
      return audioTracks[0]?.enabled || false;
    }
    return false;
  }

  // Start screen sharing
  async shareScreen() {
    try {
      if (this.localScreenStream) {
        // Already sharing
        return this.localScreenStream;
      }
      
      this.localScreenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });
      
      // Replace video track in all peer connections
      this.peerConnections.forEach((pc, hostId) => {
        const sender = pc.getSenders().find(s => 
          s.track && s.track.kind === 'video'
        );
        if (sender) {
          sender.replaceTrack(this.localScreenStream.getVideoTracks()[0]);
        }
      });
      
      // Handle screen share end
      this.localScreenStream.getVideoTracks()[0].onended = () => {
        this.stopScreenShare();
      };
      
      this.notifyMediaStateChange();
      return this.localScreenStream;
    } catch (error) {
      console.error('Failed to start screen share:', error);
      throw error;
    }
  }

  // Stop screen sharing
  async stopScreenShare() {
    if (this.localScreenStream) {
      this.localScreenStream.getTracks().forEach(track => track.stop());
      this.localScreenStream = null;
      
      // Replace with camera track if available
      if (this.localStream) {
        this.peerConnections.forEach((pc, hostId) => {
          const sender = pc.getSenders().find(s => 
            s.track && s.track.kind === 'video'
          );
          if (sender) {
            sender.replaceTrack(this.localStream.getVideoTracks()[0]);
          }
        });
      }
      
      this.notifyMediaStateChange();
    }
  }

  // Create peer connection for a specific host
  createPeerConnection(hostId) {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    
    // Handle remote stream
    pc.ontrack = (event) => {
      console.log('Received remote track from host:', hostId);
      this.onRemoteStream?.(hostId, event.streams[0]);
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendWebSocketMessage({
          type: 'webrtc-ice-candidate',
          data: {
            hostId,
            candidate: event.candidate,
          }
        });
      }
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log(`Connection state with ${hostId}:`, pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        this.onHostLeft?.(hostId);
      }
    };
    
    // Add local stream if available
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        pc.addTrack(track, this.localStream);
      });
    }
    
    this.peerConnections.set(hostId, pc);
    return pc;
  }

  // Handle new host joining
  async handleHostJoined(hostInfo) {
    console.log('Host joined:', hostInfo);
    
    // Don't create connection to self
    if (hostInfo.userId === this.hostInfo?.userId) {
      return;
    }
    
    // Create peer connection
    const pc = this.createPeerConnection(hostInfo.userId);
    
    // Create and send offer if we're the initiator (lower user ID)
    if (this.hostInfo?.userId < hostInfo.userId) {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        this.sendWebSocketMessage({
          type: 'webrtc-offer',
          data: {
            targetHostId: hostInfo.userId,
            fromHostId: this.hostInfo.userId,
            offer: offer,
          }
        });
      } catch (error) {
        console.error('Failed to create offer for host:', hostInfo.userId, error);
      }
    }
    
    this.onHostJoined?.(hostInfo);
  }

  // Handle host leaving
  handleHostLeft(hostInfo) {
    console.log('Host left:', hostInfo);
    
    const pc = this.peerConnections.get(hostInfo.userId);
    if (pc) {
      pc.close();
      this.peerConnections.delete(hostInfo.userId);
    }
    
    this.onHostLeft?.(hostInfo.userId);
  }

  // Handle WebRTC offer
  async handleWebRTCOffer(data) {
    try {
      const { fromHostId, offer } = data;
      
      let pc = this.peerConnections.get(fromHostId);
      if (!pc) {
        pc = this.createPeerConnection(fromHostId);
      }
      
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      this.sendWebSocketMessage({
        type: 'webrtc-answer',
        data: {
          targetHostId: fromHostId,
          fromHostId: this.hostInfo.userId,
          answer: answer,
        }
      });
    } catch (error) {
      console.error('Failed to handle WebRTC offer:', error);
    }
  }

  // Handle WebRTC answer
  async handleWebRTCAnswer(data) {
    try {
      const { fromHostId, answer } = data;
      
      const pc = this.peerConnections.get(fromHostId);
      if (pc) {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
      }
    } catch (error) {
      console.error('Failed to handle WebRTC answer:', error);
    }
  }

  // Handle ICE candidate
  async handleWebRTCIceCandidate(data) {
    try {
      const { fromHostId, candidate } = data;
      
      const pc = this.peerConnections.get(fromHostId);
      if (pc) {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (error) {
      console.error('Failed to handle ICE candidate:', error);
    }
  }

  // Handle host media state changes
  handleHostMediaStateChanged(data) {
    console.log('Host media state changed:', data);
    this.onHostMediaStateChanged?.(data.hostId, data.mediaState);
  }

  // Notify other hosts about media state change
  notifyMediaStateChange() {
    const mediaState = {
      camera: this.localStream && this.localStream.getVideoTracks().length > 0 && this.localStream.getVideoTracks()[0].enabled,
      microphone: this.localStream && this.localStream.getAudioTracks().length > 0 && this.localStream.getAudioTracks()[0].enabled,
      screenShare: this.localScreenStream && this.localScreenStream.getVideoTracks().length > 0,
    };
    
    this.sendWebSocketMessage({
      type: 'host-media-state',
      data: {
        hostId: this.hostInfo?.userId,
        mediaState: mediaState,
      }
    });
  }

  // Send chat message
  sendChatMessage(message) {
    this.sendWebSocketMessage({
      type: 'chat-message',
      data: {
        hostId: this.hostInfo?.userId,
        hostName: this.hostInfo?.displayName,
        message: message,
        timestamp: new Date().toISOString(),
      }
    });
  }

  // Send message via WebSocket
  sendWebSocketMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message:', message);
    }
  }

  // Notify about host removal (for host managers)
  sendHostRemoval(hostId) {
    this.sendWebSocketMessage({
      type: 'remove-host',
      data: {
        hostId: hostId,
        removedBy: this.hostInfo?.userId,
      }
    });
  }

  // Get current media state
  getMediaState() {
    return {
      camera: this.localStream && this.localStream.getVideoTracks().length > 0 && this.localStream.getVideoTracks()[0].enabled,
      microphone: this.localStream && this.localStream.getAudioTracks().length > 0 && this.localStream.getAudioTracks()[0].enabled,
      screenShare: this.localScreenStream && this.localScreenStream.getVideoTracks().length > 0,
      hasLocalStream: !!this.localStream,
      connectedHosts: this.peerConnections.size,
    };
  }

  // Legacy methods for backward compatibility with existing CameraShare component
  async joinStream_legacy(streamId, userType) {
    // This is a simplified version for backward compatibility
    return this.joinStream(streamId, userType, {
      userId: `user-${Date.now()}`,
      displayName: 'User',
      role: userType === 'host' ? 'host' : 'viewer',
    });
  }

  async getStreamSignals(streamId) {
    try {
      const response = await axios.get(`${this.baseURL}/api/streams/${streamId}/signals`);
      return response.data;
    } catch (error) {
      console.error('Failed to get stream signals:', error);
      return [];
    }
  }

  async sendWebRTCOffer(streamId, offer) {
    try {
      const response = await axios.post(`${this.baseURL}/api/streams/${streamId}/webrtc/offer`, {
        offer: offer,
        clientId: this.clientId || 'anonymous',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to send WebRTC offer:', error);
      throw error;
    }
  }

  async sendWebRTCAnswer(streamId, answer) {
    try {
      const response = await axios.post(`${this.baseURL}/api/streams/${streamId}/webrtc/answer`, {
        answer: answer,
        clientId: this.clientId || 'anonymous',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to send WebRTC answer:', error);
      throw error;
    }
  }

  async sendWebRTCIceCandidate(streamId, candidate) {
    try {
      const response = await axios.post(`${this.baseURL}/api/streams/${streamId}/webrtc/ice-candidate`, {
        candidate: candidate,
        clientId: this.clientId || 'anonymous',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to send ICE candidate:', error);
      throw error;
    }
  }
}

export const webrtcService = new WebRTCService(); 